import { type PropsWithChildren, useEffect, useRef, useState } from "react";

import {
  Button,
  ChevronLeftSVG,
  ChevronRightSVG,
  Icon,
  useWindowSize,
} from "@newsela/angelou";

import {
  horizontalContainerCss,
  scrollablePanel,
  scrollLeftCss,
  scrollRightCss,
} from "./ScrollablePanel.style";

/**
 * The number of movements to complete a step width
 */
const SCROLL_MOVEMENTS = 10 as const;

interface ScrollablePanelProps {
  /**
   * Number of px to scroll the bar (it may be one card's width)
   */
  stepWidth: number;
}

const ScrollablePanel = (props: PropsWithChildren<ScrollablePanelProps>) => {
  const { stepWidth, children } = props;
  const { width: windowWidth } = useWindowSize();
  const panelRef = useRef<HTMLDivElement>(null);

  const slidePanel = (direction: number) => {
    const container = panelRef?.current;
    let scrollCounter = 0;
    if (container) {
      const slideVar = setInterval(function () {
        container.scrollLeft += (stepWidth / SCROLL_MOVEMENTS) * direction;
        scrollCounter += 1;
        if (scrollCounter >= SCROLL_MOVEMENTS) {
          window.clearInterval(slideVar);
        }
      }, 50);
    }
  };

  const [isVisibleNavButtons, setVisibleNavButtons] = useState<boolean>(false);

  useEffect(() => {
    const { current } = panelRef;
    setVisibleNavButtons(
      !!current && current.scrollWidth > current.clientWidth,
    );
  }, [children, windowWidth]);

  return (
    <div css={scrollablePanel}>
      {isVisibleNavButtons && (
        <Button
          ariaProps={{
            "aria-label": "Scroll left",
          }}
          flavor={Button.flavors.incognito}
          onClick={() => {
            slidePanel(-1);
          }}
          __cssFor={{ root: scrollLeftCss }}
        >
          <Icon size={Icon.sizes.md} SvgComponent={ChevronLeftSVG} />
        </Button>
      )}
      <div css={horizontalContainerCss} ref={panelRef}>
        {children}
      </div>
      {isVisibleNavButtons && (
        <Button
          ariaProps={{
            "aria-label": "Scroll Right",
          }}
          legacy_flavor={Button.flavors.incognito}
          onClick={() => {
            slidePanel(1);
          }}
          __cssFor={{ root: scrollRightCss }}
        >
          <Icon size={Icon.sizes.md} SvgComponent={ChevronRightSVG} />
        </Button>
      )}
    </div>
  );
};

export default ScrollablePanel;
